import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import Confetti from 'react-confetti';
import { Button, Box } from '@mantine/core';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Html, Image} from '@react-three/drei';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { IconCaretLeftFilled, IconCaretRightFilled } from '@tabler/icons-react';
import { styled } from '@mantine/core';
// Camera configuration object
const CAMERA_CONFIG = {
  position: new THREE.Vector3(14, 3, -3),
  rotation: new THREE.Euler(-2, 1.245, 2.436900278131925, 'XYZ'),
  fov: 60
};

const WHEEL_RADIUS = 5;
const WHEEL_THICKNESS = 2;
const EDGE_THICKNESS = 0.2;
const FULL_ROTATION = Math.PI * 2;
const SPIN_ROTATIONS = 8; // Number of full rotations before slowing down
const SPIN_DURATION = 5000; // Total spin duration in milliseconds

// Add this new constant for the glow effect
const GLOW_INTENSITY = 0.5;

// Add this custom shader for the glow effect
const glowShader = {
  uniforms: {
    color: { value: new THREE.Color(0xffff00) },
    glowIntensity: { value: GLOW_INTENSITY },
  },
  vertexShader: `
    varying vec3 vNormal;
    void main() {
      vNormal = normalize(normalMatrix * normal);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform vec3 color;
    uniform float glowIntensity;
    varying vec3 vNormal;
    void main() {
      float intensity = pow(glowIntensity - dot(vNormal, vec3(0.0, 0.0, 1.0)), 4.0);
      gl_FragColor = vec4(color, intensity);
    }
  `,
};

const DEFAULT_PRIZES = [
  { name: "Prize 1", image: "/prizeimages/backpack.png" },
  { name: "Prize 2", image: "/prizeimages/backpack.png" },
  { name: "Prize 3", image: "/prizeimages/backpack.png" },
  { name: "Prize 4", image: "/prizeimages/backpack.png" },
  { name: "Prize 5", image: "/prizeimages/backpack.png" },
  { name: "Prize 6", image: "/prizeimages/backpack.png" },
  { name: "Prize 7", image: "/prizeimages/backpack.png" },
  { name: "Prize 8", image: "/prizeimages/backpack.png" },
  { name: "Prize 9", image: "/prizeimages/backpack.png" },
  { name: "Prize 10", image: "/prizeimages/backpack.png" },
  { name: "Prize 11", image: "/prizeimages/backpack.png" },
  { name: "Prize 12", image: "/prizeimages/backpack.png" },
];

function useImageAvailable(url) {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!url) {
      setAvailable(false);
      return;
    }

    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (!cancelled) setAvailable(true);
    };
    img.onerror = () => {
      if (!cancelled) setAvailable(false);
    };
    img.src = url;

    return () => {
      cancelled = true;
    };
  }, [url]);

  return available;
}

function PrizeSegmentImage({ url, ...props }) {
  const available = useImageAvailable(url);
  if (!available) return null;
  return <Image url={url} {...props} />;
}

const UGButton = ({ children, ...props }) => (
  <Button
    {...props}
    styles={(theme) => ({
      root: {
        backgroundColor: '#f7b500',
        color: '#1c1c1c',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        borderRadius: 0,
        padding: '10px 20px', // Adjusted padding
        '&:hover': {
          backgroundColor: '#ffffff',
        },
      },
      label: {
        color: '#1c1c1c',
        fontSize: '16px', // Adjusted font size
      },
    })}
  >
    {children}
  </Button>
);

const Wheel = ({ prizes, spinning, onSpinComplete, winningPrizeIndex }) => {
  const wheelRef = useRef();
  const [rotation, setRotation] = useState(0);
  const spinStartTime = useRef(null);
  const targetRotation = useRef(0);
  const [geometryReady, setGeometryReady] = useState(false);

  // Handle empty prizes gracefully
  if (!prizes || prizes.length === 0) {
    return null;
  }

  const segmentAngle = (Math.PI * 2) / prizes.length;

  const cartoonyColors = useMemo(() => {
    // First half: Red to Peach (8 even steps)
    const firstHalf = [
      '#152F3C', '#234C5D', '#32697D', '#42879B',
      '#58A1B4', '#7BB4C0', '#9CC6CD', '#BDD8DB'
    ];
    
    // Second half: Reverse the middle colors only (exclude both first and last colors)
    const secondHalf = firstHalf.slice(1, -1).reverse();
    
    // Add a slightly different red at the end to ensure the last segment is distinct
    return [...firstHalf, ...secondHalf, '#E73E40'];
  }, []);

  const wheelGeometry = useMemo(() => {
    if (prizes.length === 0) return null;

    const geometry = new THREE.BufferGeometry();
    const vertices = [];
    const indices = [];
    const colors = [];
    const normals = [];

    for (let i = 0; i < prizes.length; i++) {
      const startAngle = i * segmentAngle;
      const endAngle = (i + 1) * segmentAngle;
      const currentColor = new THREE.Color(cartoonyColors[i % cartoonyColors.length]);
      const nextColor = new THREE.Color(cartoonyColors[(i + 1) % cartoonyColors.length]);

      // Front face vertices and colors
      vertices.push(
        0, 0, WHEEL_THICKNESS / 2,
        WHEEL_RADIUS * Math.cos(startAngle), WHEEL_RADIUS * Math.sin(startAngle), WHEEL_THICKNESS / 2,
        WHEEL_RADIUS * Math.cos(endAngle), WHEEL_RADIUS * Math.sin(endAngle), WHEEL_THICKNESS / 2
      );
      for (let j = 0; j < 3; j++) {
        colors.push(currentColor.r, currentColor.g, currentColor.b);
      }

      // Back face vertices and colors
      vertices.push(
        0, 0, -WHEEL_THICKNESS / 2,
        WHEEL_RADIUS * Math.cos(startAngle), WHEEL_RADIUS * Math.sin(startAngle), -WHEEL_THICKNESS / 2,
        WHEEL_RADIUS * Math.cos(endAngle), WHEEL_RADIUS * Math.sin(endAngle), -WHEEL_THICKNESS / 2
      );
      for (let j = 0; j < 3; j++) {
        colors.push(currentColor.r, currentColor.g, currentColor.b);
      }

      // Outer face vertices and colors (using both current and next colors)
      vertices.push(
        WHEEL_RADIUS * Math.cos(startAngle), WHEEL_RADIUS * Math.sin(startAngle), WHEEL_THICKNESS / 2,
        WHEEL_RADIUS * Math.cos(startAngle), WHEEL_RADIUS * Math.sin(startAngle), -WHEEL_THICKNESS / 2,
        WHEEL_RADIUS * Math.cos(endAngle), WHEEL_RADIUS * Math.sin(endAngle), -WHEEL_THICKNESS / 2,
        WHEEL_RADIUS * Math.cos(endAngle), WHEEL_RADIUS * Math.sin(endAngle), WHEEL_THICKNESS / 2
      );
      // Apply current color to the start vertices and next color to the end vertices
      colors.push(
        currentColor.r, currentColor.g, currentColor.b,
        currentColor.r, currentColor.g, currentColor.b,
        nextColor.r, nextColor.g, nextColor.b,
        nextColor.r, nextColor.g, nextColor.b
      );

      // Normals (same as before)
      normals.push(
        0, 0, 1, 0, 0, 1, 0, 0, 1,  // front
        0, 0, -1, 0, 0, -1, 0, 0, -1,  // back
        Math.cos((startAngle + endAngle) / 2), Math.sin((startAngle + endAngle) / 2), 0,  // sides
        Math.cos((startAngle + endAngle) / 2), Math.sin((startAngle + endAngle) / 2), 0,
        Math.cos((startAngle + endAngle) / 2), Math.sin((startAngle + endAngle) / 2), 0,
        Math.cos((startAngle + endAngle) / 2), Math.sin((startAngle + endAngle) / 2), 0
      );

      const baseIndex = i * 10;
      indices.push(
        // Front face
        baseIndex, baseIndex + 1, baseIndex + 2,
        // Back face
        baseIndex + 3, baseIndex + 5, baseIndex + 4,
        // Outer face
        baseIndex + 6, baseIndex + 7, baseIndex + 8,
        baseIndex + 6, baseIndex + 8, baseIndex + 9
      );
    }

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(indices);

    setGeometryReady(true);
    return geometry;
  }, [prizes, segmentAngle, cartoonyColors]);

  const edgeGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const vertices = [];
    const indices = [];

    for (let i = 0; i < prizes.length; i++) {
      const angle = i * segmentAngle;
      const x = WHEEL_RADIUS * Math.cos(angle);
      const y = WHEEL_RADIUS * Math.sin(angle);

      vertices.push(
        x, y, WHEEL_THICKNESS / 2,
        x, y, -WHEEL_THICKNESS / 2,
        x * (1 + EDGE_THICKNESS / WHEEL_RADIUS), y * (1 + EDGE_THICKNESS / WHEEL_RADIUS), WHEEL_THICKNESS / 2,
        x * (1 + EDGE_THICKNESS / WHEEL_RADIUS), y * (1 + EDGE_THICKNESS / WHEEL_RADIUS), -WHEEL_THICKNESS / 2
      );

      const baseIndex = i * 4;
      indices.push(baseIndex, baseIndex + 1, baseIndex + 2, baseIndex + 1, baseIndex + 3, baseIndex + 2);
    }

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }, [prizes, segmentAngle]);

  useEffect(() => {
    if (spinning) {
      spinStartTime.current = Date.now();
      const prizesCount = prizes.length;
      // Adjust the angle calculation for opposite direction
      const angleToWinningPrize = ((winningPrizeIndex + 0) % prizesCount) * (Math.PI * 2 / prizesCount);
      // Negate the rotation to spin in the opposite direction
      targetRotation.current = -(SPIN_ROTATIONS * Math.PI * 2 + angleToWinningPrize);
    }
  }, [spinning, prizes.length, winningPrizeIndex]);

  useFrame(() => {
    if (spinning) {
      const elapsedTime = Date.now() - spinStartTime.current;
      const progress = Math.min(elapsedTime / SPIN_DURATION, 1);
      
      // Easing function for smooth deceleration
      const easeOut = (t) => 1 - Math.pow(1 - t, 3);
      
      const currentRotation = easeOut(progress) * targetRotation.current;
      setRotation(currentRotation);

      if (progress === 1) {
        onSpinComplete();
      }
    }
  });

  if (!geometryReady) {
    return null;
  }

  return (
    <group ref={wheelRef} rotation={[0, 0, rotation]}>
      {/* Colored segments (including outer faces) */}
      <mesh geometry={wheelGeometry}>
        <meshStandardMaterial vertexColors side={THREE.DoubleSide} />
      </mesh>

      {/* Prize names on the rim */}
      {prizes.map((prize, index) => (
        <group key={index} rotation={[0, 0, index * segmentAngle + segmentAngle / 2]}>
          <PrizeSegmentImage
            url={prize.image}
            position={[WHEEL_RADIUS * 0.99, -.3, 0]}
            rotation={[0, Math.PI / 2, 0]}
            scale={[WHEEL_RADIUS * 0.28, WHEEL_RADIUS * 0.28, 1]}
            transparent
            opacity={0.85}
          />
          <Text
            position={[WHEEL_RADIUS * .98, 0.7, 0]}
            rotation={[0, Math.PI / 2, 0]}
            fontSize={0.2}
            color="black"
            anchorX="center"
            anchorY="middle"
            maxWidth={WHEEL_THICKNESS * 0.9}
            font="/fonts/Roboto-Regular.ttf"
            textAlign="center">
            {prize.name.toUpperCase()}
          </Text>
        </group>
      ))}

      {/* White edges */}
      <mesh geometry={edgeGeometry}>
        <meshBasicMaterial color="white" side={THREE.DoubleSide} />
      </mesh>
    </group>
  )
};

export function PrizeWheel({ instance, prizes, spinTrigger, onResetInactivity, onScreensaverOff }) {
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [winningPrizeIndex, setWinningPrizeIndex] = useState(null);
  const [cameraPosition, setCameraPosition] = useState(CAMERA_CONFIG.position);
  const [cameraRotation, setCameraRotation] = useState(CAMERA_CONFIG.rotation);
  const [expandingPrize, setExpandingPrize] = useState(null);
  const [resultImageError, setResultImageError] = useState(false);

  const controls = useAnimation();

  useEffect(() => {
    setResultImageError(false);
  }, [expandingPrize]);

  useEffect(() => {
    controls.start({
      scale: [1, 1.2, 1],
      rotate: [0, 3, 0],
      x: [0, 20, 0],
      y: [0, 10, 0],
      transition: {
        duration: 15,
        ease: "easeInOut",
        times: [0, 0.5, 1],
        repeat: Infinity,
      }
    });
  }, [controls]);

  useEffect(() => {
    const svg = document.getElementById('background-svg');
    if (svg) {
      svg.style.transition = 'transform 15s ease-in-out';
      controls.start((i) => ({
        transform: svg.style.transform,
      }));
    }
  }, [controls]);

  useEffect(() => {
    if (spinTrigger) {
      startSpin(spinTrigger.prizeIndex, spinTrigger.prize);
    }
  }, [spinTrigger]);

  const startSpin = useCallback((prizeIndex, prize) => {
    setWinningPrizeIndex(prizeIndex);
    setResult(prize);
    setSpinning(true);
    setShowPrizeModal(false);
    setExpandingPrize(null);
    setShowConfetti(false);
    if (onScreensaverOff) {
      onScreensaverOff();
    }
  }, [onScreensaverOff]);

  const handleSpinComplete = useCallback(() => {
    setSpinning(false);
    if (onResetInactivity) {
      onResetInactivity();
    }

    if (result) {
      setExpandingPrize(result);
      setShowPrizeModal(true);
      setShowConfetti(true);

      const timer = setTimeout(() => {
        setShowPrizeModal(false);
        setShowConfetti(false);
        setExpandingPrize(null);
        setResult(null);
        setWinningPrizeIndex(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [result, onResetInactivity]);

  const handleLogPosition = (position, rotation) => {
    setCameraPosition({ x: position.x, y: position.y, z: position.z });
    setCameraRotation({ x: rotation.x, y: rotation.y, z: rotation.z });
  };

  const handleLogCameraPosition = () => {
    console.log('Camera Position:', cameraPosition);
    console.log('Camera Rotation:', cameraRotation);
  };

  //console.log("Render - spinning:", spinning, "showResult:", showResult, "showConfetti:", showConfetti);

  return (
    <div
      style={{
        position: 'relative',
        width: '50%',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: '#ffffff'
      }}
    >
      {prizes === null ? (
        <div>Loading prizes...</div>
      ) : prizes.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#666',
          textAlign: 'center',
          padding: '40px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎁</div>
          <h2 style={{ color: '#333', marginBottom: '10px' }}>No Prizes Available</h2>
          <p style={{ color: '#666', lineHeight: '1.5' }}>
            This event doesn't have any prizes yet.<br/>
            Add prizes in the admin panel to enable the wheel.
          </p>
        </div>
      ) : (
        <Canvas
          style={{ 
            width: '100%', 
            height: '100%',
            position: 'relative',
            zIndex: 1
          }}
          camera={{
            position: [cameraPosition.x, cameraPosition.y, cameraPosition.z],
            rotation: [cameraRotation.x, cameraRotation.y, cameraRotation.z],
            fov: CAMERA_CONFIG.fov
          }}
          onContextMenu={(e) => e.stopPropagation()} // Add this line to enable right-click
        >
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} intensity={1} />
          <directionalLight position={[-5, 5, 5]} intensity={0.5} />
          <Wheel 
            prizes={prizes} 
            spinning={spinning} 
            onSpinComplete={handleSpinComplete} 
            winningPrizeIndex={winningPrizeIndex} 
          />
          <WheelMarkers />
          <CameraControls 
            onLogPosition={handleLogPosition} 
            initialPosition={CAMERA_CONFIG.position} 
            initialRotation={CAMERA_CONFIG.rotation} 
          />
        </Canvas>
      )}

      {/* Result modal */}
      <AnimatePresence>
        {showPrizeModal && expandingPrize && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: instance === 1 ? 0 : '50%',
              width: '50%',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              style={{
                backgroundColor: '#FBF4D8',
                borderRadius: '3rem',
                border: '3px solid #ffffff',
                boxShadow: `
                  0 0 60px 20px rgba(254, 183, 109, 0.3),
                  0 0 100px 40px rgba(254, 183, 109, 0.2),
                  0 0 140px 60px rgba(254, 183, 109, 0.1)
                `,
                textAlign: 'center',
                maxWidth: '90%',
                width: '33rem',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(45deg, #163456, #2B657B)',
                  padding: '30px 20px',
                  borderBottom: '3px solid #ffffff'
                }}>
                <h2
                  style={{
                    color: 'white',
                    fontSize: '1em',
                    marginBottom: '10px',
                    marginTop: '10px',
                    fontWeight: 'bold',
                    fontFamily: 'Roboto, sans-serif',
                    textTransform: 'uppercase'
                  }}>
                  You won
                </h2>
                <h1
                  style={{
                    color: 'white',
                    fontSize: '1.75em',
                    marginBottom: '10px',
                    fontFamily: 'Figtree, sans-serif',
                    fontWeight: 'bold',
                    fontStyle: 'italic'
                  }}>
                  {expandingPrize.name}
                </h1>
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 100,
                  damping: 20,
                  delay: 0.3
                }}
                style={{ 
                  padding: '3rem',
                  display: 'flex',
                  justifyContent: 'center',
                  perspective: '1000px' // Add this for 3D effect
                }}>
                {!resultImageError && expandingPrize.image && (
                  <motion.img
                    src={expandingPrize.image}
                    alt={expandingPrize.name}
                    initial={{ rotateY: -180 }}
                    animate={{ rotateY: 0 }}
                    transition={{
                      type: 'spring',
                      stiffness: 50,
                      damping: 20,
                      delay: 0.3
                    }}
                    onError={() => setResultImageError(true)}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '10px',
                      maxHeight: '200px',
                      display: 'block',
                      transformStyle: 'preserve-3d'
                    }}
                  />
                )}
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confetti */}
      {showConfetti && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: instance === 1 ? '0' : '50%', 
          width: '50%', 
          height: '100%' 
        }}>
          <Confetti width={window.innerWidth / 2} height={window.innerHeight} />
        </div>
      )}

      {/* Add this at the end, just before the closing div tag */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
    </div>
  )
}

function CameraControls({ onLogPosition, initialPosition, initialRotation }) {
  const { camera, gl } = useThree();
  const controlsRef = useRef();

  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.object.position.set(initialPosition.x, initialPosition.y, initialPosition.z);
      controlsRef.current.object.rotation.set(initialRotation.x, initialRotation.y, initialRotation.z);
      controlsRef.current.update();
    }
  }, [initialPosition, initialRotation]);

  return (
    <OrbitControls
      ref={controlsRef}
      args={[camera, gl.domElement]}
      enableRotate={true}
      enableZoom={false}
      enablePan={false}
      minPolarAngle={Math.PI / 4}
      maxPolarAngle={Math.PI / 1.5}
    />
  );
}

function WheelMarkers() {
  return (
    <group rotation={[0, 0, 5]}>
      <Html
        position={[0, WHEEL_RADIUS, -WHEEL_THICKNESS / 2 - 0.2]}
        center
        style={{ zIndex: 1 }}
      >
        <IconCaretLeftFilled size={64} color="black" />
      </Html>
      <Html
        position={[0, WHEEL_RADIUS, WHEEL_THICKNESS / 2 + 0.2]}
        center
        style={{ zIndex: 1 }}
      >
        <IconCaretRightFilled size={64} color="black" />
      </Html>
    </group>
  )
}
